import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import {
  CognitoUnauthorizedError,
  isCognitoUserAuthenticated,
} from './useCognitoUserIsAuthenticated'
import { throwApiError } from '../Helpers/utils'
import { DevicesResponse, devicesResponseSchema } from '@mobi/api-types'

type UseMfaDevicesOptions<TData> = Omit<
  UseQueryOptions<DevicesResponse, Error, TData>,
  'queryKey' | 'queryFn' | 'retry'
>

export function useMfaDevices<TData = DevicesResponse>(
  accountNumber?: number,
  options?: UseMfaDevicesOptions<TData>
) {
  const { enabled, ...otherOptions } = options ?? { enabled: true }
  return useQuery<DevicesResponse, Error, TData>({
    queryKey: mfaDevicesQueryKey(accountNumber),
    queryFn: () => fetchDevices(),
    retry: 1,
    enabled: enabled && !!accountNumber,
    ...otherOptions,
  })
}

export function mfaDevicesQueryKey(accountNumber?: number) {
  return ['mfa-devices', accountNumber ?? 'logged-out']
}

async function fetchDevices(): Promise<DevicesResponse> {
  const isAuthenticated = await isCognitoUserAuthenticated(true)
  if (!isAuthenticated) {
    throw new CognitoUnauthorizedError()
  }

  try {
    const response = await fetch(`/api/mfa/get-user-devices`)
    if (!response.ok) {
      throw new Error('Failed to fetch devices')
    }
    const json = await response.json()
    return devicesResponseSchema.parse(json)
  } catch (error: unknown) {
    return throwApiError(error)
  }
}
